import '@react-sigma/core/lib/style.css';

// Import Sigma type and SigmaContainer
import { SigmaContainer } from '@react-sigma/core';
import { FC } from 'react';

// Import Loader
import { Loader } from '@/components/Loader';
import {
  SIGMA_CONTAINER_STYLE,
  SIGMA_SETTINGS,
} from '@/components/nodeGraph/config/graphConfig';
import {
  GraphInstanceProvider,
  GraphStateProvider,
} from '@/components/nodeGraph/core';
import { GraphErrorBoundary } from '@/components/nodeGraph/GraphErrorBoundary';
import { GraphLoader } from '@/components/nodeGraph/GraphLoader';
import { GraphEntityType } from '@/components/nodeGraph/utils/types';
import { Filters } from '@/hooks/useAssets';
import { SeedFilter } from '@/hooks/useSeeds';

interface NodeGraphProps {
  clusterByEntity?: GraphEntityType;
  setClusterByEntity?: (entityType: GraphEntityType) => void;
  filters?: Filters | SeedFilter;
  setFilters?: (filters: Filters | SeedFilter) => void;
}

/**
 * NodeGraph - Main Graph Visualization Component
 *
 * This is the top-level component that renders the interactive asset relationship graph.
 * It provides a Sigma.js container with optimized settings for force-directed layouts
 * and handles the overall graph lifecycle including loading states and empty states.
 *
 * Features:
 * - Sigma.js integration with custom node programs (border, image)
 * - Responsive canvas with proper aspect ratios
 * - Loading states with spinner
 * - Empty state handling when no assets match filters
 * - Camera controls with zoom limits (0.1x to 5x)
 * - Optimized rendering settings for performance
 *
 * The component delegates most functionality to GraphLoader while providing
 * the container and basic lifecycle management.
 */
export const NodeGraph: FC<NodeGraphProps> = ({
  clusterByEntity,
  setClusterByEntity,
  filters,
  setFilters,
}) => {
  // Convert SeedFilter to Filters if needed (for compatibility)
  const assetFilters: Filters | undefined =
    filters && 'type' in filters
      ? {
          search: filters.search,
          advancedFilters: filters.advancedFilters,
        }
      : filters;

  // Remove the asset query and early return - let GraphLoader handle filtering
  // This ensures the modal stays visible even when filters result in no assets

  // Restore the original return structure
  return (
    <GraphErrorBoundary>
      <div className="relative flex size-full flex-col">
        <div className="relative size-full">
          <SigmaContainer
            settings={SIGMA_SETTINGS}
            style={SIGMA_CONTAINER_STYLE}
          >
            <GraphStateProvider>
              <GraphInstanceProvider>
                <Loader isLoading={false} type="spinner">
                  <GraphLoader
                    clusterByEntity={clusterByEntity}
                    setClusterByEntity={setClusterByEntity}
                    filters={assetFilters}
                    setFilters={setFilters}
                  />
                </Loader>
              </GraphInstanceProvider>
            </GraphStateProvider>
          </SigmaContainer>
        </div>
      </div>
    </GraphErrorBoundary>
  );
};
